import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { fetchPublicTerms } from "@/lib/api";
import type { PublicTermsListItem } from "@/types";
import AdminButton from "@/components/admin/common/AdminButton";
import { AdminCard } from "@/components/admin/data-display/AdminCard";
import PublicTermsLayout from "./PublicTermsLayout";

const audienceLabel = (audiences: string[]) => {
  if (audiences.includes("COMMON")) return "공통";
  return audiences.map((value) => value === "KEEPER" ? "보호자" : value === "SENIOR" ? "다을 사용자" : value).join(" · ") || "공통";
};

const policyBadgeLabel = (termsTypeCode: string, requiredYn: string) => {
  if (termsTypeCode === "PRIVACY_POLICY") return "안내";
  return requiredYn === "Y" ? "필수" : "선택";
};

const PublicTermsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const userType = searchParams.get("userType") ?? "";
  const [items, setItems] = useState<PublicTermsListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    fetchPublicTerms(userType || undefined)
      .then(setItems)
      .catch((reason) => setError(reason instanceof Error ? reason.message : "약관을 불러오지 못했습니다."))
      .finally(() => setLoading(false));
  }, [userType]);

  const changeAudience = (next: string) => {
    setSearchParams(next ? { userType: next } : {});
  };

  return (
    <PublicTermsLayout>
      <section aria-labelledby="terms-list-title">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 id="terms-list-title" className="text-2xl font-bold tracking-tight">약관 목록</h1>
            <p className="mt-1 text-sm text-slate-500">현재 시행 중인 최신 약관만 표시됩니다.</p>
          </div>
          <div className="flex flex-wrap gap-2" aria-label="이용자 유형 필터">
            {[["", "전체"], ["KEEPER", "보호자"], ["SENIOR", "다을 사용자"]].map(([value, label]) => (
              <AdminButton key={label} onClick={() => changeAudience(value)} variant={userType === value ? "primary" : "secondary"} className="h-9 min-w-16">{label}</AdminButton>
            ))}
          </div>
        </div>

        {loading && <p className="mt-6 rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500">약관을 불러오는 중입니다.</p>}
        {error && <p role="alert" className="mt-6 rounded-xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700">{error}</p>}
        {!loading && !error && items.length === 0 && <p className="mt-6 rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500">공개된 약관이 없습니다.</p>}
        {!loading && !error && items.length > 0 && (
          <AdminCard className="mt-6 overflow-hidden rounded-xl">
            <ul>
              {items.map((item) => (
                <li key={`${item.termsTypeCode}-${item.version}`} className="border-b border-slate-100 last:border-0">
                  <Link to={`${item.detailPath}${userType ? `?userType=${userType}` : ""}`} className="flex min-h-24 items-center justify-between gap-4 px-5 py-4 transition hover:bg-blue-50/50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:px-6">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="font-semibold text-slate-900">{item.title}</h2>
                      <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${item.requiredYn === "Y" ? "border-blue-100 bg-blue-50 text-blue-700" : "border-slate-200 bg-slate-50 text-slate-600"}`}>{policyBadgeLabel(item.termsTypeCode, item.requiredYn)}</span>
                    </div>
                    <p className="mt-2 text-xs text-slate-500">{audienceLabel(item.audiences)} · 시행일 {item.effectiveDate} · {item.version}</p>
                  </div>
                  <span aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-lg text-slate-400">›</span>
                  </Link>
                </li>
              ))}
            </ul>
          </AdminCard>
        )}
      </section>
    </PublicTermsLayout>
  );
};

export default PublicTermsPage;
